import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiSettings, FiX, FiVolume2, FiVolumeX, FiPlay, FiPause } from 'react-icons/fi';
import { videosAPI } from '../utils/api';
import SEO from '../components/SEO';
import './Reels.css';

// Generate thumbnail URL from Cloudinary video URL
const getVideoThumbnail = (videoUrl) => {
  if (!videoUrl) return null;
  // Convert Cloudinary video URL to thumbnail
  // From: .../video/upload/v123/folder/video.mp4
  // To: .../video/upload/so_0,w_400,h_700,c_fill/v123/folder/video.jpg
  return videoUrl
    .replace('/video/upload/', '/video/upload/so_0,w_400,h_700,c_fill,q_auto/')
    .replace(/\.[^.]+$/, '.jpg');
};

const Reels = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [activeVideo, setActiveVideo] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const itemRefs = useRef({});
  const modalVideoRef = useRef(null);

  useEffect(() => {
    loadReels();
  }, []);

  const loadReels = async () => {
    try {
      const res = await videosAPI.getAll({ isReel: 'true' });
      setReels(res.videos || []);
    } catch (error) {
      console.error('Failed to load reels:', error);
      setReels([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => new Set([...prev, entry.target.dataset.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const timeoutId = setTimeout(() => {
      Object.values(itemRefs.current).forEach((ref) => {
        if (ref) observer.observe(ref);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [reels]);

  const handleMouseEnter = (e) => {
    const video = e.currentTarget.querySelector('video');
    if (video) video.play();
  };

  const handleMouseLeave = (e) => {
    const video = e.currentTarget.querySelector('video');
    if (video) video.pause();
  };

  const openVideoModal = (reel) => {
    setActiveVideo(reel);
    setIsPlaying(true);
    setIsMuted(false);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = useCallback(() => {
    setActiveVideo(null);
    document.body.style.overflow = '';
  }, []);

  const toggleMute = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.muted = !modalVideoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = () => {
    if (modalVideoRef.current) {
      if (isPlaying) {
        modalVideoRef.current.pause();
      } else {
        modalVideoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeVideoModal();
    };
    if (activeVideo) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [activeVideo, closeVideoModal]);

  if (loading) {
    return (
      <div className="reels-page">
        <SEO title="Reels" description="Video reels by Prague photographer Marta Zborovska. Fashion, beauty, behind-the-scenes. Prague videographer." path="/reels" />
        <div className="reels-header">
          <h1 className="reels-title">REELS</h1>
        </div>
        <div className="reels-container">
          <p style={{ textAlign: 'center', color: '#666' }}>Loading reels...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reels-page">
      <SEO title="Reels" description="Video reels by Prague photographer Marta Zborovska. Fashion, beauty, behind-the-scenes. Prague videographer." path="/reels" />
      <div className="reels-header">
        <h1 className="reels-title">REELS</h1>
        <p className="reels-subtitle">Click any video to play with sound</p>
      </div>
      
      {reels.length === 0 ? (
        <div className="reels-empty">
          <h2>No reels yet</h2>
          <p>Add video reels from the Admin Panel.</p>
          <Link to="/admin" className="admin-link">
            <FiSettings /> Go to Admin Panel
          </Link>
        </div>
      ) : (
        <div className="reels-container">
          <div className="reels-grid">
            {reels.map((reel) => (
              <div
                key={reel._id}
                ref={(el) => (itemRefs.current[reel._id] = el)}
                data-id={reel._id}
                className={`reel-item ${visibleItems.has(String(reel._id)) ? 'visible' : ''}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={() => openVideoModal(reel)}
              >
                {/* Thumbnail image for mobile (videos don't preload on mobile) */}
                <img 
                  src={reel.posterUrl || getVideoThumbnail(reel.url)} 
                  alt=""
                  className="reel-thumbnail"
                />
                <video
                  className="reel-video"
                  playsInline
                  muted
                  loop
                  preload="metadata"
                  poster={reel.posterUrl || getVideoThumbnail(reel.url)}
                >
                  <source src={reel.url} type="video/mp4" />
                </video>
                <div className="reel-play-overlay">
                  <FiPlay className="play-icon" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video Modal with Sound */}
      {activeVideo && (
        <div className="video-modal" onClick={closeVideoModal}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeVideoModal}>
              <FiX />
            </button>
            <video
              ref={modalVideoRef}
              className="modal-video"
              autoPlay
              loop
              playsInline
              onClick={togglePlay}
            >
              <source src={activeVideo.url} type="video/mp4" />
            </video>
            <div className="video-controls">
              <button className="control-btn" onClick={togglePlay}>
                {isPlaying ? <FiPause /> : <FiPlay />}
              </button>
              <button className="control-btn" onClick={toggleMute}>
                {isMuted ? <FiVolumeX /> : <FiVolume2 />}
              </button>
              {activeVideo.title && <span className="video-title">{activeVideo.title}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reels;
